import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Copy, KeyRound, Ban, Pencil, Trash2, Check } from 'lucide-react';

import { apiClientsApi } from '../../../api/apiClients.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';
import { ClientFormModal } from './ClientFormModal.jsx';

function SecretModal({ open, onClose, data }) {
  const copy = async (v) => { try { await navigator.clipboard.writeText(v); toast.success('Copiato.'); } catch { toast.error('Copia non riuscita.'); } };
  const row = (label, value) => (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-ink-muted">{label}</span>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded-md border border-border bg-surface-subtle px-2.5 py-2 text-xs text-ink">{value}</code>
        <Button variant="secondary" size="sm" onClick={() => copy(value)}><Copy className="h-4 w-4" /></Button>
      </div>
    </div>
  );
  return (
    <Modal open={open} onClose={onClose} title="Credenziali del client" size="md"
      footer={<Button onClick={onClose}>Ho salvato le credenziali</Button>}>
      <div className="flex flex-col gap-4">
        {data && row('Client ID', data.client?.clientId)}
        {data && row('Client secret', data.clientSecret)}
      </div>
    </Modal>
  );
}

export function ClientsPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('clients:create');
  const canUpdate = hasPermission('clients:update');
  const canRotate = hasPermission('clients:rotate');
  const canDelete = hasPermission('clients:delete');
  const queryClient = useQueryClient();
  const { run, mfaProps } = useMfaStepUp();

  const { items, isLoading, pagination, page, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['api-clients', 'list'],
    fetcher: apiClientsApi.list,
    initialLimit: 20
  });

  const [editTarget, setEditTarget] = useState(null);
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['api-clients'] });

  const rotateMutation = useMutation({
    mutationFn: (id) => run(() => apiClientsApi.rotateSecret(id)),
    onSuccess: (data) => { invalidate(); if (data && data.clientSecret) setSecret({ client: data.client, clientSecret: data.clientSecret }); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });
  const revokeMutation = useMutation({
    mutationFn: (c) => apiClientsApi.update(c._id, { status: c.status === 'active' ? 'revoked' : 'active' }),
    onSuccess: () => { toast.success('Stato aggiornato.'); invalidate(); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => run(() => apiClientsApi.remove(id)),
    onSuccess: () => { toast.success('Client eliminato.'); invalidate(); setDeleteTarget(null); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = useMemo(() => [
    {
      key: 'name', header: 'Client',
      render: (r) => (
        <div>
          <div className="font-medium text-ink">{r.name}</div>
          <code className="text-xs text-ink-faint">{r.clientId}</code>
        </div>
      )
    },
    { key: 'scopes', header: 'Scopes', render: (r) => <Badge tone="neutral">{(r.scopes || []).length} scope</Badge> },
    {
      key: 'grants', header: 'Grant', render: (r) => (
        <span className="flex flex-wrap gap-1">
          {(r.allowedGrantTypes || []).map((g) => <Badge key={g} tone="info">{g === 'client_credentials' ? 'CC' : 'RT'}</Badge>)}
        </span>
      )
    },
    { key: 'tier', header: 'Tier', render: (r) => <span className="text-xs text-ink-muted">{r.rateLimitTier || 'default'}</span> },
    { key: 'status', header: 'Stato', render: (r) => <Badge tone={r.status === 'active' ? 'success' : 'danger'}>{r.status === 'active' ? 'attivo' : 'revocato'}</Badge> },
    { key: 'lastUsedAt', header: 'Ultimo uso', render: (r) => <span className="text-xs text-ink-muted">{r.lastUsedAt ? fmt.dateTime(r.lastUsedAt) : '-'}</span> },
    {
      key: 'actions', header: '', align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {canUpdate && <button type="button" aria-label="Modifica" className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink" onClick={() => setEditTarget(r)}><Pencil className="h-4 w-4" /></button>}
          {canRotate && <button type="button" aria-label="Ruota segreto" className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink" onClick={() => rotateMutation.mutate(r._id)}><KeyRound className="h-4 w-4" /></button>}
          {canUpdate && <button type="button" aria-label={r.status === 'active' ? 'Revoca' : 'Riattiva'} className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink" onClick={() => revokeMutation.mutate(r)}>{r.status === 'active' ? <Ban className="h-4 w-4" /> : <Check className="h-4 w-4" />}</button>}
          {canDelete && <button type="button" aria-label="Elimina" className="rounded-md p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger" onClick={() => setDeleteTarget(r)}><Trash2 className="h-4 w-4" /></button>}
        </div>
      )
    }
  ], [canUpdate, canRotate, canDelete]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Client API"
        description="Credenziali OAuth2 per integrazioni di terze parti (client credentials, refresh token)."
        actions={canCreate ? <Button onClick={() => setCreating(true)}>Nuovo client</Button> : null}
      />

      <div className="flex flex-wrap items-end gap-3">
        <Input className="w-64" label="Cerca" placeholder="nome o client id..." value={filters.q || ''} onChange={(e) => setFilters({ q: e.target.value || undefined })} />
        <Select label="Stato" value={filters.status || ''} onChange={(e) => setFilters({ status: e.target.value || undefined })}>
          <option value="">Tutti</option>
          <option value="active">Attivi</option>
          <option value="revoked">Revocati</option>
        </Select>
      </div>

      <DataTable columns={columns} rows={items} loading={isLoading}
        empty={{ title: 'Nessun client API', description: 'Crea un client per consentire a un sistema esterno di chiamare le API.' }} />
      <Pagination pagination={pagination} onPageChange={setPage} />

      <ClientFormModal open={creating} onClose={() => setCreating(false)} initial={null} onCreated={(d) => setSecret(d)} />
      <ClientFormModal open={Boolean(editTarget)} onClose={() => setEditTarget(null)} initial={editTarget} />
      <SecretModal open={Boolean(secret)} onClose={() => setSecret(null)} data={secret} />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminare il client?"
        description={deleteTarget ? `Il client "${deleteTarget.name}" e i suoi token saranno eliminati. Le integrazioni che lo usano smetteranno di funzionare.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget._id)}
      />

      <MfaPrompt {...mfaProps} />
    </div>
  );
}
